if (!customElements.get('media-gallery')) {
  customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        constructor() {
          super();

          this.sectionId = this.dataset.sectionId;
          this.autoRotate = this.dataset.autoRotate === 'true';
          this.changeSlidesSpeed = this.dataset.changeSlidesSpeed || '5000';
          this.imageZoom = this.dataset.imageZoom;

          this.mainSwiper = null;
          this.thumbsSwiper = null;

          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]') || this.querySelector('.swiper-main'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]') || this.querySelector('.swiper-thumbs'),
            modalOpeners: this.querySelectorAll('.product__modal-opener'),
            thumbnailButtons: this.querySelectorAll('[data-target] button'),
          };

          this.mql = window.matchMedia('(min-width: 768px)');

          // Initialization Swiper
          this.initializeSwiper();
        }

        initializeSwiper() {
          const thumbsSwiperEl = this.querySelector(`#thumbsSwiper-${this.sectionId}`);
          const mainSwiperEl = this.querySelector(`#mainSwiper-${this.sectionId}`);

          if (!thumbsSwiperEl || !mainSwiperEl) {
            console.warn('Swiper elements not found');
            return;
          }

          this.thumbsSwiper = new Swiper(thumbsSwiperEl, {
            spaceBetween: 10,
            slidesPerView: 4,
            freeMode: true,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
            breakpoints: {
              0: {
                slidesPerView: 4.5,
                spaceBetween: 12
              },
              767: {
                slidesPerView: 4.5,
                spaceBetween: 12
              },
            },
          });

          this.mainSwiper = new Swiper(mainSwiperEl, {
            spaceBetween: 10,
            loop: false,
            thumbs: {
              swiper: this.thumbsSwiper
            },
            zoom: {
              maxRatio: 3,
              minRatio: 1,
              toggle: this.imageZoom !== 'lightbox',
            },
            ...(this.autoRotate && {
              autoplay: {
                delay: parseInt(this.changeSlidesSpeed),
                disableOnInteraction: false,
              },
            }),
          });

          // save reference
          this._mainSwiper = this.mainSwiper;
          this._thumbsSwiper = this.thumbsSwiper;

          this.init();
        }

        init() {
          // Bind modal opener
          this.bindModalOpeners();

          if (this.elements.thumbnailButtons.length > 0) {
            this.bindThumbnailClicks();
          }

          // Setup Swiper features
          if (this.mainSwiper) {
            this.setupSwiperFeatures();
          }
        }

        // Modal lightbox
        bindModalOpeners() {
          this.elements.modalOpeners.forEach(opener => {
            opener.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              const slide = opener.closest('[data-media-id]');
              if (!slide) return;

              let mediaId = slide.dataset.mediaId;
              if (!mediaId) return;

              // Remove prefix (for main-product)
              if (this.sectionId && mediaId.includes(this.sectionId)) {
                mediaId = mediaId.replace(`${this.sectionId}-`, '');
              }

              opener.dataset.mediaId = mediaId;

              const modal = document.querySelector(opener.dataset.modal);
              if (modal?.show) {
                modal.show(opener);
              }
            });
          });
        }

        // Thumbnail click
        bindThumbnailClicks() {
          this.elements.thumbnailButtons.forEach(button => {
            const mediaToSwitch = button.closest('[data-target]');
            if (!mediaToSwitch) return;

            button.addEventListener('click', (e) => {
              e.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
        }

        // Swiper features
        setupSwiperFeatures() {
          this.mainSwiper.on('slideChange', () => {
            const status = this.elements.liveRegion;
            if (status) {
              status.textContent = `Slide ${this.mainSwiper.activeIndex + 1} of ${this.mainSwiper.slides.length}`;
            }

            // Sync active class for thumbnail
            if (this.thumbsSwiper) {
              const activeIndex = this.mainSwiper.activeIndex;
              this.thumbsSwiper.slides.forEach((slide, i) => {
                slide.classList.toggle('swiper-slide-thumb-active', i === activeIndex);
              });
            }

            // Dispatch custom event
            this.dispatchEvent(new CustomEvent('slideChanged', {
              detail: {
                currentElement: this.mainSwiper.slides[this.mainSwiper.activeIndex]
              }
            }));
          });
        }

        // setActiveMedia
        setActiveMedia(mediaId, prepend = false) {
          const activeMedia = this.elements.viewer.querySelector(`[data-media-id*="${mediaId}"]`);
          if (!activeMedia) return;

          this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => {
            el.classList.remove('is-active');
          });
          activeMedia.classList.add('is-active');

          if (this.mainSwiper) {
            const slideIndex = Array.from(this.mainSwiper.slides).findIndex(slide =>
                slide.dataset.mediaId?.includes(mediaId)
            );
            if (slideIndex >= 0) {
              this.mainSwiper.slideTo(slideIndex);
            }
          }

          // Prevent sticky header
          this.preventStickyHeader();

          // Scroll to media
          setTimeout(() => {
            const rect = activeMedia.getBoundingClientRect();
            if (rect.top < 0) {
              window.scrollTo({
                top: rect.top + window.scrollY - 100,
                behavior: 'smooth'
              });
            }
          }, 100);

          // Load deferred media
          this.playActiveMedia(activeMedia);

          // Sync thumbnail
          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target*="${mediaId}"]`);
          if (thumbnail) this.setActiveThumbnail(thumbnail);
        }

        setActiveThumbnail(thumbnail) {
          if (!thumbnail) return;
          this.elements.thumbnails?.querySelectorAll('button').forEach(btn => {
            btn.removeAttribute('aria-current');
          });
          thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        }

        playActiveMedia(activeItem) {
          window.pauseAllMedia();
          const deferred = activeItem.querySelector('deferred-media');
          if (deferred) deferred.loadContent(false);
        }

        preventStickyHeader() {
          const sticky = document.querySelector('sticky-header');
          if (sticky) {
            sticky.dispatchEvent(new CustomEvent('preventHeaderReveal'));
          }
        }
      }
  );
}